import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Zap } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get('/billing/plans').then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const upgrade = async (plan) => {
    if (!plan.lookup_key) return;
    setBusy(plan.key);
    try {
      const { data } = await api.post('/payments/checkout', { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setBusy(null);
    }
  };

  const pct = user ? Math.min(100, Math.round((user.usage_minutes / user.usage_limit) * 100)) : 0;

  return (
    <div>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-2">Billing & Usage</h1>
      <p className="text-slate-400 mb-8">You are on the <span className="text-white font-semibold">{user?.plan_name}</span> plan.</p>

      <div className="rounded-2xl border border-white/10 bg-[#12141D] p-6 mb-8">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-xs uppercase tracking-widest text-slate-500">Monthly usage</span>
          <span className="font-mono text-sm text-slate-300">{user?.usage_minutes} / {user?.usage_limit} minutes</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/8 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#FF4D00] to-[#8B5CF6]" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs text-slate-500 mt-2 font-mono">Resets {user?.usage_reset_at ? new Date(user.usage_reset_at).toLocaleDateString() : '—'}</div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((p) => {
          const current = user?.plan === p.key;
          return (
            <div key={p.key} className={`rounded-2xl border p-6 flex flex-col ${current ? 'border-[#FF4D00]/50 bg-[#161926]' : 'border-white/10 bg-[#12141D]'}`}>
              <div className="font-heading font-semibold text-lg">{p.name}</div>
              <div className="mt-2"><span className="font-heading text-3xl font-extrabold">${p.price}</span><span className="text-slate-500 text-sm">/mo</span></div>
              <ul className="space-y-2 mt-5 flex-1 text-sm">
                {p.features.slice(0, 5).map((f) => <li key={f} className="flex items-start gap-2 text-slate-300"><Check className="w-4 h-4 text-[#FF884D] mt-0.5 shrink-0" />{f}</li>)}
              </ul>
              {current ? (
                <Button disabled className="w-full mt-6 rounded-full opacity-60">Current plan</Button>
              ) : p.price === 0 ? (
                <Button disabled variant="outline" className="w-full mt-6 rounded-full border-white/15 bg-white/5 opacity-60">Free</Button>
              ) : (
                <Button data-testid={`upgrade-${p.key}`} onClick={() => upgrade(p)} disabled={busy === p.key} className="w-full mt-6 rounded-full font-semibold">
                  {busy === p.key ? 'Redirecting…' : <><Zap className="w-4 h-4 mr-1" /> Upgrade</>}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
